import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, ExternalLink, Save, Send, Eye } from "lucide-react";
import { api, getToken, setToken, resolveImg, formatApiError } from "@/lib/adminApi";
import ImageField from "@/components/admin/ImageField";
import RichEditor from "@/components/admin/RichEditor";

const TABS = [
  { key: "articles", label: "Articles" },
  { key: "picks", label: "Top Shelf Products" },
];

const CATEGORIES = ["whiskey", "spirits", "glassware", "apparel", "barware"];

const EMPTY_ARTICLE = { title: "", slug: "", dek: "", body: "", category: "Feature", author: "", reading_time: "5 min", image_url: "", chapter_no: "", status: "draft" };
const EMPTY_PICK = { category: "whiskey", name: "", detail: "", price: "", affiliate_url: "", image_url: "", score: "", abv: "", issue: "", reviewer: "", review_category: "", review_body: "", badge_unicorn: false, badge_legend: false };

function slugifyClient(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="eyebrow">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full bg-transparent border-b outline-none py-2 font-body text-base text-bone";
const inputStyle = { borderColor: "var(--brass)", cursor: "text" };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [picks, setPicks] = useState([]);
  const [editing, setEditing] = useState(null); // {type, data} | null

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    api.get("/auth/me").then(() => { setReady(true); load(); }).catch(() => { setToken(null); navigate("/admin/login"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      const [a, p] = await Promise.all([api.get("/admin/articles"), api.get("/picks")]);
      setArticles(a.data);
      setPicks(p.data);
    } catch (err) {
      if (err.response?.status === 401) { setToken(null); navigate("/admin/login"); }
    }
  }

  function logout() {
    setToken(null);
    navigate("/admin/login");
  }

  function openNew() {
    setEditing({ type: tab, data: tab === "articles" ? { ...EMPTY_ARTICLE } : { ...EMPTY_PICK }, isNew: true });
  }

  function openEdit(type, data) {
    setEditing({ type, data: { ...data }, isNew: false });
  }

  // status override: 'draft' | 'published' | undefined (keep as-is)
  async function save(statusOverride) {
    const { type, data, isNew } = editing;
    const base = type === "articles" ? "articles" : "picks";
    const payload = { ...data };
    if (type === "picks") {
      payload.score =
        payload.score === "" || payload.score === null || payload.score === undefined ? null : Number(payload.score);
    }
    if (type === "articles") {
      if (statusOverride) payload.status = statusOverride;
      if (!payload.title?.trim()) { toast.error("Add a title first"); return; }
      payload.slug = slugifyClient(payload.slug || payload.title);
    }
    try {
      let saved;
      if (isNew) saved = (await api.post(`/admin/${base}`, payload)).data;
      else saved = (await api.put(`/admin/${base}/${data.id}`, payload)).data;
      if (type === "articles" && statusOverride === "published") toast.success("Published");
      else if (type === "articles" && statusOverride === "draft") toast.success("Saved as draft");
      else toast.success(isNew ? "Created" : "Saved");
      // keep editing the just-saved record (so subsequent saves are updates)
      if (type === "articles") setEditing({ type, data: saved, isNew: false });
      else setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  }

  async function remove(type, id) {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    const base = type === "articles" ? "articles" : "picks";
    try {
      await api.delete(`/admin/${base}/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  }

  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center bg-bg text-bone eyebrow">Unlocking the cellar…</div>;
  }

  const list = tab === "articles" ? articles : picks;

  return (
    <div className="min-h-screen bg-bg text-bone" style={{ cursor: "auto" }}>
      {/* Top bar */}
      <header className="border-b sticky top-0 z-20 nav-blur" style={{ borderColor: "var(--hairline)" }}>
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg width="24" height="24" viewBox="0 0 40 40" fill="none">
              <path d="M8 8 L20 32 L32 8" stroke="var(--brass)" strokeWidth="1.4" />
              <circle cx="20" cy="20" r="3" fill="var(--amber)" />
            </svg>
            <span className="font-display text-lg">Johnny Dram · Cellar</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="eyebrow hover:text-[var(--amber)]" data-testid="admin-view-site">
              View Site <ExternalLink size={12} className="inline" />
            </a>
            <button onClick={logout} className="btn-outline" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-10">
        {/* Tabs + New */}
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div className="flex gap-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`px-5 py-2 font-accent uppercase tracking-widest text-sm border ${tab === t.key ? "text-bone" : "text-bone-dim"}`}
                style={{ borderColor: "var(--hairline)", background: tab === t.key ? "var(--surface-2)" : "transparent" }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button onClick={openNew} className="btn-solid" data-testid="admin-new-item">
            <Plus size={16} /> New {tab === "articles" ? "Article" : "Product"}
          </button>
        </div>

        {/* List */}
        <div className="flex flex-col divide-y" style={{ borderColor: "var(--hairline)" }}>
          {list.length === 0 && (
            <div className="py-16 text-center eyebrow" style={{ color: "var(--bone-dim)" }}>Nothing here yet.</div>
          )}
          {list.map((item) => (
            <div key={item.id} className="flex items-center gap-5 py-5" data-testid={`admin-row-${item.id}`} style={{ borderColor: "var(--hairline)" }}>
              <div className="w-16 h-16 flex-none border overflow-hidden" style={{ borderColor: "var(--hairline)", background: "#0B0B08" }}>
                {item.image_url && <img src={resolveImg(item.image_url)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl truncate text-bone flex items-center gap-3">
                  {tab === "articles" ? item.title : item.name}
                  {tab === "articles" && (
                    <span className={`status-pill ${item.status}`} data-testid={`status-${item.id}`}>{item.status}</span>
                  )}
                </div>
                <div className="eyebrow mt-1" style={{ color: "var(--bone-dim)" }}>
                  {tab === "articles" ? `${item.category} · ${item.author}` : `${item.category} · ${item.price}`}
                </div>
              </div>
              <button onClick={() => openEdit(tab, item)} className="btn-outline" data-testid={`admin-edit-${item.id}`}>
                <Pencil size={13} /> Edit
              </button>
              <button
                onClick={() => remove(tab, item.id)}
                className="p-2 border hover:text-[var(--amber)] transition-colors"
                style={{ borderColor: "var(--hairline)" }}
                data-testid={`admin-delete-${item.id}`}
                aria-label="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </main>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-30 flex justify-end" data-testid="admin-editor">
          <div className="absolute inset-0" style={{ background: "rgba(8,7,6,0.7)" }} onClick={() => setEditing(null)} />
          <div className={`relative w-full ${editing.type === "articles" ? "max-w-3xl" : "max-w-xl"} h-full overflow-y-auto border-l p-8`} style={{ borderColor: "var(--brass)", background: "var(--surface)" }}>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-display text-3xl text-bone">
                {editing.isNew ? "New" : "Edit"} {editing.type === "articles" ? "Article" : "Product"}
              </h2>
              <button onClick={() => setEditing(null)} data-testid="admin-editor-close" className="p-2 border" style={{ borderColor: "var(--hairline)" }}>
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-col gap-6">
              <ImageField value={editing.data.image_url} onChange={(url) => setEditing((s) => ({ ...s, data: { ...s.data, image_url: url } }))} testid="editor-image" />

              {editing.type === "articles" ? (
                <>
                  <Field label="Title">
                    <input className={inputCls} style={inputStyle} value={editing.data.title} data-testid="editor-title"
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, title: e.target.value } }))} />
                  </Field>
                  <Field label="Dek (subtitle)">
                    <input className={inputCls} style={inputStyle} value={editing.data.dek} data-testid="editor-dek"
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, dek: e.target.value } }))} />
                  </Field>
                  <Field label="URL slug">
                    <input className={inputCls} style={inputStyle} value={editing.data.slug ?? ""} placeholder="auto-generated from title" data-testid="editor-slug"
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, slug: e.target.value } }))}
                      onBlur={(e) => setEditing((s) => ({ ...s, data: { ...s.data, slug: slugifyClient(e.target.value) } }))} />
                  </Field>
                  <Field label="Body">
                    <RichEditor
                      value={editing.data.body}
                      onChange={(html) => setEditing((s) => ({ ...s, data: { ...s.data, body: html } }))}
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-6">
                    <Field label="Category">
                      <input className={inputCls} style={inputStyle} value={editing.data.category} data-testid="editor-category"
                        onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, category: e.target.value } }))} />
                    </Field>
                    <Field label="Chapter N° (optional)">
                      <input className={inputCls} style={inputStyle} value={editing.data.chapter_no ?? ""} data-testid="editor-chapter"
                        onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, chapter_no: e.target.value } }))} />
                    </Field>
                    <Field label="Author">
                      <input className={inputCls} style={inputStyle} value={editing.data.author} data-testid="editor-author"
                        onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, author: e.target.value } }))} />
                    </Field>
                    <Field label="Reading time">
                      <input className={inputCls} style={inputStyle} value={editing.data.reading_time} data-testid="editor-reading-time"
                        onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, reading_time: e.target.value } }))} />
                    </Field>
                  </div>
                </>
              ) : (
                <>
                  <Field label="Category">
                    <select className={`${inputCls} appearance-none`} style={{ ...inputStyle, cursor: "pointer" }} value={editing.data.category} data-testid="editor-pick-category"
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, category: e.target.value } }))}>
                      {CATEGORIES.map((c) => (<option key={c} value={c} style={{ background: "#17150F" }}>{c}</option>))}
                    </select>
                  </Field>
                  <Field label="Name">
                    <input className={inputCls} style={inputStyle} value={editing.data.name} data-testid="editor-name"
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, name: e.target.value } }))} />
                  </Field>
                  <Field label="Detail">
                    <input className={inputCls} style={inputStyle} value={editing.data.detail} data-testid="editor-detail"
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, detail: e.target.value } }))} />
                  </Field>
                  <div className="grid grid-cols-2 gap-6">
                    <Field label="Price (e.g. $68)">
                      <input className={inputCls} style={inputStyle} value={editing.data.price} data-testid="editor-price"
                        onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, price: e.target.value } }))} />
                    </Field>
                    {(editing.data.category === "whiskey" || editing.data.category === "spirits") && (
                      <Field label="Score (0–10, optional)">
                        <input
                          type="number" min="0" max="10" step="0.1"
                          className={inputCls} style={inputStyle}
                          value={editing.data.score ?? ""}
                          placeholder="e.g. 9.4"
                          data-testid="editor-score"
                          onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, score: e.target.value } }))}
                        />
                      </Field>
                    )}
                  </div>
                  <Field label="Amazon Affiliate Link">
                    <input className={inputCls} style={inputStyle} value={editing.data.affiliate_url} placeholder="https://www.amazon.com/…?tag=yourtag-20" data-testid="editor-affiliate"
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, affiliate_url: e.target.value } }))} />
                  </Field>

                  {(editing.data.category === "whiskey" || editing.data.category === "spirits") && (
                    <div className="border-t pt-6 flex flex-col gap-6" style={{ borderColor: "var(--hairline)" }}>
                      <div className="eyebrow" style={{ color: "var(--amber)" }}>Detailed Review (shown on the review page)</div>
                      <div className="grid grid-cols-2 gap-6">
                        <Field label="ABV (e.g. 59.45%)">
                          <input className={inputCls} style={inputStyle} value={editing.data.abv ?? ""} placeholder="43%" data-testid="editor-abv"
                            onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, abv: e.target.value } }))} />
                        </Field>
                        <Field label="Issue (e.g. Fall 2020)">
                          <input className={inputCls} style={inputStyle} value={editing.data.issue ?? ""} data-testid="editor-issue"
                            onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, issue: e.target.value } }))} />
                        </Field>
                        <Field label="Reviewer">
                          <input className={inputCls} style={inputStyle} value={editing.data.reviewer ?? ""} data-testid="editor-reviewer"
                            onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, reviewer: e.target.value } }))} />
                        </Field>
                        <Field label="Review Category (e.g. American Whiskey)">
                          <input className={inputCls} style={inputStyle} value={editing.data.review_category ?? ""} data-testid="editor-review-category"
                            onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, review_category: e.target.value } }))} />
                        </Field>
                      </div>
                      <Field label="Review / Tasting Notes">
                        <textarea rows={7} className={`${inputCls} resize-y`} style={inputStyle} value={editing.data.review_body ?? ""}
                          placeholder="A sweet and enticing nose of marzipan, crème anglaise… (use blank lines to separate paragraphs)"
                          data-testid="editor-review-body"
                          onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, review_body: e.target.value } }))} />
                      </Field>

                      <div className="flex flex-col gap-3">
                        <label className="eyebrow">Award Badges (shown on the product image)</label>
                        <div className="flex flex-wrap gap-3">
                          {[["badge_unicorn", "Unicorn", "unicorn"], ["badge_legend", "Legend", "legend"]].map(([key, label, img]) => {
                            const on = !!editing.data[key];
                            return (
                              <button
                                key={key}
                                type="button"
                                data-testid={`editor-${key.replace("_", "-")}`}
                                onClick={() => setEditing((s) => ({ ...s, data: { ...s.data, [key]: !s.data[key] } }))}
                                className="flex items-center gap-3 px-4 py-2 border transition-colors"
                                style={{ borderColor: on ? "var(--amber)" : "var(--hairline)", background: on ? "var(--surface-2)" : "transparent" }}
                              >
                                <img src={`/badges/${img}.jpg`} alt={label} style={{ width: 34, height: 34, borderRadius: "9999px", opacity: on ? 1 : 0.4 }} />
                                <span className="font-accent text-sm uppercase tracking-widest" style={{ color: on ? "var(--bone)" : "var(--bone-dim)" }}>
                                  {label}{on ? " ✓" : ""}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        <p className="font-body text-sm text-bone-dim italic">Toggle none, either, or both. Both stack without overlapping.</p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {editing.type === "articles" ? (
                <div className="flex flex-wrap gap-3 pt-4 sticky bottom-0 py-4" style={{ background: "var(--surface)" }}>
                  <button onClick={() => save("draft")} className="btn-outline justify-center" data-testid="admin-save-draft">
                    <Save size={14} /> Save Draft
                  </button>
                  <button onClick={() => save("published")} className="btn-solid flex-1 justify-center" data-testid="admin-publish">
                    <Send size={14} /> {editing.data.status === "published" ? "Update & Publish" : "Publish"}
                  </button>
                  {editing.data.status === "published" && editing.data.slug && (
                    <a href={`/journal/${editing.data.slug}`} target="_blank" rel="noreferrer" className="btn-outline justify-center" data-testid="admin-view-article">
                      <Eye size={14} /> View
                    </a>
                  )}
                  <button onClick={() => setEditing(null)} className="btn-outline justify-center" data-testid="admin-editor-cancel">Close</button>
                </div>
              ) : (
                <div className="flex gap-4 pt-4">
                  <button onClick={() => save()} className="btn-solid flex-1 justify-center" data-testid="admin-editor-save">Save</button>
                  <button onClick={() => setEditing(null)} className="btn-outline justify-center" data-testid="admin-editor-cancel">Cancel</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
